import { peek, effect } from 'maverick.js';
import { isUndefined, isNumber, DOMEvent, listenEvent, camelToKebabCase } from 'maverick.js/std';
import { F as ENABLE_AUTO_QUALITY, G as TextTrack, H as TEXT_TRACK_READY_STATE, J as TEXT_TRACK_ON_MODE_CHANGE, L as LIST_SELECT, K as SET_AUTO_QUALITY, N as LIST_ADD, P as IS_CHROME } from '../../media-core.js';

class RAFLoop {
  constructor(_callback) {
    this._e = _callback;
  }
  Lh;
  M() {
    if (!isUndefined(this.Lh))
      return;
    this.Mh();
  }
  N() {
    if (isNumber(this.Lh))
      window.cancelAnimationFrame(this.Lh);
    this.Lh = void 0;
  }
  Mh() {
    this.Lh = window.requestAnimationFrame(() => {
      if (isUndefined(this.Lh))
        return;
      this._e();
      this.Mh();
    });
  }
}

const toDOMEventType = (type) => camelToKebabCase(type);
class HLSController {
  constructor(_video) {
    this.ta = _video;
  }
  ph;
  Ca = null;
  uh = null;
  nh = {};
  oh = /* @__PURE__ */ new Set();
  get instance() {
    return this.Ca;
  }
  setup(ctor, context) {
    this.ph = context;
    const isLive = peek(context.$store.streamType).includes("live"), isLiveLowLatency = peek(context.$store.streamType).includes("ll-");
    this.Ca = new ctor({
      lowLatencyMode: isLiveLowLatency,
      backBufferLength: isLiveLowLatency ? 4 : isLive ? 8 : void 0,
      renderTextTracksNatively: false,
      ...this.nh
    });
    const dispatcher = this.vh.bind(this);
    for (const event of Object.values(ctor.Events))
      this.Ca.on(event, dispatcher);
    this.Ca.on(ctor.Events.ERROR, this.Zb.bind(this));
    for (const callback of this.oh)
      callback(this.Ca);
    context.player.dispatchEvent(new DOMEvent("hls-instance", { detail: this.Ca }));
    this.Ca.attachMedia(this.ta);
    this.Ca.on(ctor.Events.AUDIO_TRACK_SWITCHED, this.wh.bind(this));
    this.Ca.on(ctor.Events.LEVEL_SWITCHED, this.xh.bind(this));
    this.Ca.on(ctor.Events.LEVEL_LOADED, this.yh.bind(this));
    this.Ca.on(ctor.Events.NON_NATIVE_TEXT_TRACKS_FOUND, this.zh.bind(this));
    this.Ca.on(ctor.Events.CUES_PARSED, this.Ah.bind(this));
    context.qualities[ENABLE_AUTO_QUALITY] = this.Bh.bind(this);
    listenEvent(context.qualities, "change", this.Rf.bind(this));
    listenEvent(context.audioTracks, "change", this.Ch.bind(this));
    this.uh = effect(this.Dh.bind(this));
  }
  Dh() {
    if (!this.ph.$store.live())
      return;
    const raf = new RAFLoop(this.Eh.bind(this));
    raf.M();
    return raf.N.bind(raf);
  }
  Eh() {
    this.ph.$store.liveSyncPosition.set(this.Ca?.liveSyncPosition ?? Infinity);
  }
  vh(eventType, detail) {
    this.ph.player.dispatchEvent(new DOMEvent(toDOMEventType(eventType), { detail }));
  }
  zh(eventType, data) {
    const event = new DOMEvent(eventType, { detail: data });
    let currentTrack = -1;
    for (let i = 0; i < data.tracks.length; i++) {
      const nonNativeTrack = data.tracks[i], init = nonNativeTrack.subtitleTrack ?? nonNativeTrack.closedCaptions, track = new TextTrack({
        id: `hls-${nonNativeTrack.kind}${i}`,
        src: init?.url,
        label: nonNativeTrack.label,
        language: init?.lang,
        kind: nonNativeTrack.kind
      });
      track[TEXT_TRACK_READY_STATE] = 2;
      track[TEXT_TRACK_ON_MODE_CHANGE] = () => {
        if (track.mode === "showing") {
          this.Ca.subtitleTrack = i;
          currentTrack = i;
        } else if (currentTrack === i) {
          this.Ca.subtitleTrack = -1;
          currentTrack = -1;
        }
      };
      if (nonNativeTrack.default)
        track.setMode("showing", event);
      this.ph.textTracks.add(track, event);
    }
  }
  Ah(eventType, data) {
    const track = this.ph.textTracks.getById(`hls-${data.track}`);
    if (!track)
      return;
    const event = new DOMEvent(eventType, { detail: data });
    for (const cue of data.cues) {
      cue.positionAlign = "auto";
      track.addCue(cue, event);
    }
  }
  wh(eventType, data) {
    const track = this.ph.audioTracks[data.id];
    if (track) {
      this.ph.audioTracks[LIST_SELECT](
        track,
        true,
        new DOMEvent(eventType, { detail: data })
      );
    }
  }
  xh(eventType, data) {
    const quality = this.ph.qualities[data.level];
    if (quality) {
      this.ph.qualities[LIST_SELECT](
        quality,
        true,
        new DOMEvent(eventType, { detail: data })
      );
    }
  }
  yh(eventType, data) {
    if (this.ph.$store.canPlay())
      return;
    const { type, live, totalduration: duration } = data.details;
    const event = new DOMEvent(eventType, { detail: data });
    this.ph.delegate.U("stream-type-change", {
      detail: live ? type === "EVENT" && Number.isFinite(duration) ? "live:dvr" : "live" : "on-demand",
      trigger: event
    });
    this.ph.delegate.U("duration-change", { detail: duration, trigger: event });
    const media = this.Ca.media;
    if (this.Ca.currentLevel === -1) {
      this.ph.qualities[SET_AUTO_QUALITY](true, event);
    }
    for (const track of this.Ca.audioTracks) {
      this.ph.audioTracks[LIST_ADD](
        {
          id: track.id + "",
          label: track.name,
          language: track.lang || "",
          kind: "main"
        },
        event
      );
    }
    for (const level of this.Ca.levels) {
      this.ph.qualities[LIST_ADD](
        {
          width: level.width,
          height: level.height,
          codec: level.codecSet,
          bitrate: level.bitrate
        },
        event
      );
    }
    media.dispatchEvent(new DOMEvent("canplay", { trigger: event }));
  }
  Zb(eventType, data) {
    if (data.fatal) {
      switch (data.type) {
        case "networkError":
          this.Ca?.startLoad();
          break;
        case "mediaError":
          this.Ca?.recoverMediaError();
          break;
        default:
          this.Ca?.destroy();
          this.Ca = null;
          break;
      }
    }
  }
  Bh() {
    if (this.Ca)
      this.Ca.currentLevel = -1;
  }
  Rf() {
    const { qualities } = this.ph;
    if (!this.Ca || qualities.auto)
      return;
    this.Ca[qualities.switch + "Level"] = qualities.selectedIndex;
    if (IS_CHROME)
      this.ta.currentTime = this.ta.currentTime;
  }
  Ch() {
    const { audioTracks } = this.ph;
    if (this.Ca && this.Ca.audioTrack !== audioTracks.selectedIndex) {
      this.Ca.audioTrack = audioTracks.selectedIndex;
    }
  }
  Jg() {
    if (this.ph)
      this.ph.qualities[ENABLE_AUTO_QUALITY] = void 0;
    this.Ca?.destroy();
    this.Ca = null;
    this.uh?.();
    this.uh = null;
  }
}

export { HLSController as H, RAFLoop as R };
